import React, { useState, useEffect } from "react";
import { Dialog } from '../dialogs/dialog';
import { Button } from "primereact/button";
import { Checkbox } from "primereact/checkbox";
import { Slider } from "primereact/slider";
import { ColorPicker } from "primereact/colorpicker";
import { Dropdown } from "primereact/dropdown";
import { useThemesData } from "../../providers/theme-management-provider";
import { parseCssGradient, serializeGradient, GradientConfig, GradientStop } from "../../general/utilities";

export const BodyStylesDialog: React.FC<{ visible: boolean; onHide: () => void }> = ({ visible, onHide }) => {
    const { findCssValue, bulkUpdateCssProperties, deleteCssProperty } = useThemesData();
    const [useGradient, setUseGradient] = useState(false);
    const [gradient, setGradient] = useState<GradientConfig>({ type: 'linear', angle: 180, stops: [] });

    useEffect(() => {
        if (visible) {
            const rawBg = findCssValue("body", "background") || "";
            const isGrad = rawBg.includes("gradient");
            setUseGradient(isGrad);
            if (isGrad) setGradient(parseCssGradient(rawBg));
            else setGradient({ type: 'linear', angle: 180, stops: [{ id: '0', color: '#ffffff', position: 0 }, { id: '1', color: '#000000', position: 100 }] });
        }
    }, [visible, findCssValue]);

    const handleApply = () => {
        if (useGradient) {
            bulkUpdateCssProperties("body", [{ property: "background", value: serializeGradient(gradient) }], 'merge');
        } else {
            deleteCssProperty("body", "background");
        }
        onHide();
    };

    const addStop = () => {
        setGradient(prev => {
            const newStops = [...prev.stops, { id: Date.now().toString(), color: '#ffffff', position: 100 }];
            const total = newStops.length;

            // Re-distribute all stops evenly across 0-100%
            const redistributed = newStops.map((stop, index) => ({
                ...stop,
                position: Math.round((100 / (total - 1)) * index)
            }));

            return { ...prev, stops: redistributed };
        });
    };
    
    const updateStop = (id: string, field: keyof GradientStop, value: any) => {
        setGradient(prev => ({
            ...prev,
            stops: prev.stops.map(s => s.id === id ? { ...s, [field]: value } : s)
        }));
    };

    return (
        <Dialog header="Body styles" visible={visible} onHide={onHide} style={{ width: '500px' }}>
            <div className="p-fluid">
                <div className="field-checkbox mb-4">
                    <Checkbox inputId="gradToggle" checked={useGradient} onChange={e => setUseGradient(!!e.checked)} />
                    <label htmlFor="gradToggle" className="font-bold ml-2">Enable background gradient</label>
                </div>

                {/* Always rendered, but disabled if !useGradient */}
                <div style={{ 
                    opacity: useGradient ? 1 : 0.4, 
                    pointerEvents: useGradient ? 'auto' : 'none',
                    transition: 'opacity 0.2s'
                }} className="p-3 surface-50 border-round border-1 border-200">
                    <div className="flex gap-2 mb-3">
                        <Dropdown className="w-6" value={gradient.type} options={['linear', 'radial']} onChange={(e) => setGradient({...gradient, type: e.value})} />
                        {gradient.type === 'linear' && (
                            <div className="w-6">
                                <label className="text-xs font-bold block mb-1">Angle: {gradient.angle}°</label>
                                <Slider value={gradient.angle} min={0} max={360} onChange={e => setGradient({...gradient, angle: e.value as number})} />
                            </div>
                        )}
                    </div>

                    {gradient.stops.map((stop) => (
                        <div key={stop.id} className="flex align-items-center gap-2 mb-2">
                            <ColorPicker inputStyle={{ width: '40px' }} value={stop.color.replace('#', '')} onChange={(e) => updateStop(stop.id, 'color', `#${e.value}`)} />
                            <Slider className="flex-grow-1" value={stop.position} onChange={(e) => updateStop(stop.id, 'position', e.value)} />
                            <span className="text-xs font-mono w-3rem text-right">{stop.position}%</span>
                            <Button icon="pi pi-trash" className="p-button-danger p-button-sm" onClick={() => setGradient(prev => ({ ...prev, stops: prev.stops.filter(s => s.id !== stop.id) }))} />
                        </div>
                    ))}
                    <Button label="Add stop" icon="pi pi-plus" className="p-button-outlined p-button-sm mt-2" onClick={addStop} />
                    <div className="mt-4 p-2 border-round" style={{ 
                        height: '60px', 
                        background: useGradient ? serializeGradient(gradient) : 'transparent',
                        border: '1px solid #ccc' 
                    }}>
                        <span className="text-xs text-500">Live preview</span>
                    </div>
                </div>
            </div>
            <div className="admin-toolbar flex justify-content-end mt-4 gap-2">
                <Button label="Cancel" className="p-button-secondary" onClick={onHide} />
                <Button label="Apply" onClick={handleApply} />
            </div>
        </Dialog>
    );
};